/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import React, { useMemo } from "react"
import { useAtom as GetAtom } from "jotai"
import classNames from "classnames"
import { ArrowRight } from "lucide-react"
import { Button } from "@malond/ui/components/button"
import {
  RequestSaleCreateOne,
  RequestSaleCreateOneItem,
  SaleApi,
  SaleCreateOneRequest,
} from "../../libs/api/backend/src"
import { createConfiguration } from "../../libs/api/openApi"
import {
  useEnterpriseGetOne,
  useMainEnterpriseId,
} from "../../hooks/enterprise"
import { useShopGetOne, useShopId } from "../../hooks/shop"
import {
  checkoutDiscountAtom,
  checkoutItemsAtom,
  checkoutNextButtonDisabledAtom,
  checkoutStepAtom,
  checkoutSumBackAtom,
  checkoutSumGiveAtom,
  checkoutSumRaiseAtom,
  checkoutSumTotalAtom,
} from "../../store/checkout"
import { loadingSaleAtom } from "../../store/loading"
import { useShopSalePointId } from "../../hooks/shopSalePoint"
import { useShopPaymentPointId } from "../../hooks/shopPaymentPoint"

export default function CheckoutNextButton() {
  const clientAPI = useMemo(() => new SaleApi(createConfiguration()), [])

  const enterpriseId = useMainEnterpriseId()
  const shopId = useShopId()
  const shopSalePointId = useShopSalePointId()
  const shopPaymentPointId = useShopPaymentPointId()

  const [checkoutItems] = GetAtom(checkoutItemsAtom)
  const [checkoutSumRaise] = GetAtom(checkoutSumRaiseAtom)
  const [checkoutSumTotal] = GetAtom(checkoutSumTotalAtom)
  const [checkoutDiscount] = GetAtom<string>(checkoutDiscountAtom)
  const [checkoutSumGive] = GetAtom(checkoutSumGiveAtom)
  const [checkoutSumBack] = GetAtom(checkoutSumBackAtom)

  const [checkoutStep, setCheckoutStep] = GetAtom(checkoutStepAtom)
  const [checkoutNextButtonDisabled] = GetAtom(checkoutNextButtonDisabledAtom)
  const [, setIsLoading] = GetAtom(loadingSaleAtom)

  const { isLoading: isLoadingEnterprise, isError: isErrorEnterprise } =
    useEnterpriseGetOne(enterpriseId)
  const { isLoading: isLoadingShop, isError: isErrorShop } = useShopGetOne(
    enterpriseId,
    shopId
  )

  if (
    isLoadingEnterprise ||
    isErrorEnterprise ||
    isLoadingShop ||
    isErrorShop
  ) {
    return null
  }

  const onClickNext = async () => {
    if (checkoutNextButtonDisabled) {
      return false
    }

    if (checkoutStep === "0") {
      await setCheckoutStep(String(Number(checkoutStep) + 1))
      return false
    }

    if (checkoutStep === "1") {
      try {
        await setIsLoading(true)

        const now = new Date()

        let countItems = 0
        let priceItems = 0
        let priceItemsBuy = 0
        let priceItemsDiscount = 0
        const priceDiscount = Number(checkoutDiscount) || 0
        const sumRaise = Number(checkoutSumRaise) || 0
        const priceFinal = Number(checkoutSumTotal) || 0
        const sumGive = Number(checkoutSumGive) || 0
        const sumBack = Number(checkoutSumBack) || 0

        let itemsForSale: Array<RequestSaleCreateOneItem> = []

        for (const checkoutItem of checkoutItems) {
          const count = Number(checkoutItem.count) || 0
          countItems += count
          priceItems += (Number(checkoutItem.priceOut) || 0) * count
          priceItemsBuy += (Number(checkoutItem.priceBuy) || 0) * count
          priceItemsDiscount +=
            (Number(checkoutItem.priceDiscount) || 0) * count

          itemsForSale.push({
            id: checkoutItem.id,
            countOfSell: String(count),
          })
        }

        const requestSaleCreateOne: RequestSaleCreateOne = {
          sale: {
            shopPaymentPointId: shopPaymentPointId,
            countItems: String(countItems),
            priceItems: String(Math.round(priceItems * 100) / 100),
            priceItemsBuy: String(Math.round(priceItemsBuy * 100) / 100),
            priceItemsDiscount: String(
              Math.round(priceItemsDiscount * 100) / 100
            ),
            priceDiscount: String(Math.round(priceDiscount * 100) / 100),
            sumRaise: String(Math.round(sumRaise * 100) / 100),
            priceFinal: String(Math.round(priceFinal * 100) / 100),
            sumGive: String(Math.round(sumGive * 100) / 100),
            sumBack: String(Math.round(sumBack * 100) / 100),
            dateOperation: parseInt(`${now.getTime() / 1000}`),
          },
          items: itemsForSale,
        }

        const saleCreateOneRequest: SaleCreateOneRequest = {
          enterpriseId: enterpriseId,
          requestSaleCreateOne: requestSaleCreateOne,
        }

        // Create sale
        const { success, data } = await clientAPI.saleCreateOne(
          saleCreateOneRequest,
          { credentials: "include" }
        )

        if (!success) {
          console.log("!success. data:", data)
          return
        }

        await setCheckoutStep(String(Number(checkoutStep) + 1))
      } catch (e) {
        console.error(e)
      } finally {
        await setIsLoading(false)
      }

      return false
    }
  }

  return (
    <div
      className={classNames("px-4 py-4 sm:px-6", {
        hidden: checkoutStep === "2",
      })}
    >
      <Button
        type="button"
        size="lg"
        className="h-12 w-full cursor-pointer text-base font-semibold"
        disabled={checkoutNextButtonDisabled}
        onClick={onClickNext}
      >
        Далее
        <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  )
}
